import * as React from 'react'
import {
  ALL_FILTER_BLOCK_TYPES,
  AtLeast,
  Block,
  BlockType,
  DateBlock,
  LIST_VIEW_FILTER_TYPES,
  ListBlock,
  ListView,
  isComponentBlock,
} from '@basehub/data/replicache/index'

import { Button } from '~/sotano-kit/primitives/button'
import { Input } from '~/sotano-kit/primitives/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { useSelectedView } from '../use-selected-view'
import { Cross1Icon, PlusIcon } from '@radix-ui/react-icons'
import { getId } from '@basehub/utils/id/index'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { getFilterInputProps } from '../../helpers/filter'
import { BlockIcon } from '~/components/block-icon'
import { invariant } from '@epic-web/invariant'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/sotano-kit/primitives/popover'
import { useEditorMode } from '../../../../editor-mode-provider'
import { ToggleState } from '~/hooks/use-toggle-state'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  RepoUndoOps,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import { UpdateListViewParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/list'
import { useTabsStore } from '../../../../tabs/store'

export const ListBlockFilterPopover = ({
  block,
  path,
  toggleState,
  children,
}: {
  block: ListBlock
  parent: Block
  path: string
  toggleState: ToggleState
  children: React.ReactNode
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const blockTreeManager = useWorkingTreeStore((s) => s.tree)
  const editorMode = useEditorMode()
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const focusOpTable = React.useMemo(() => {
    return {
      op: 'focus',
      args: {
        focus: {
          type: 'block:wrapper',
          blockId: sourceBlockId,
        },
        preferredTabKey: activeTabKey,
      },
    } satisfies RepoUndoOps[number]
  }, [activeTabKey, sourceBlockId])

  const [selectedView, updateSelectedView] = useSelectedView(block)

  const targetBlockId_withinTree =
    blockTreeManager?.getNode(block.typeOptions.targetBlockId)?.value.id ?? ''

  const possibleBlocksToFilter = React.useMemo(() => {
    if (!blockTreeManager) return []

    const template = blockTreeManager.getNode(
      block.typeOptions.targetBlockId
    )?.value

    invariant(
      template,
      `List template could not be found: ${block.typeOptions.targetBlockId}`
    )
    invariant(
      isComponentBlock(template),
      'List template should be a component block'
    )

    const possibleBlocks: AtLeast<Block, 'id' | 'type' | 'title'>[] = [
      {
        id: '__title',
        type: 'text',
        title: 'Title',
      },
      {
        id: '__hidden',
        type: 'boolean',
        title: 'Hidden',
      },
    ]

    blockTreeManager
      .getChildrenKeys(idToSourceBlockId(template))
      .forEach((childId) => {
        const childBlock = blockTreeManager?.getNode(childId)?.value
        if (!childBlock) return

        if (!ALL_FILTER_BLOCK_TYPES.includes(childBlock.type)) {
          return
        }

        possibleBlocks.push(childBlock)
      })

    return possibleBlocks
  }, [block.typeOptions.targetBlockId, blockTreeManager])

  const hasFilters = (selectedView?.filter?.length ?? 0) > 0

  return (
    <Popover open={toggleState.isOn} onOpenChange={toggleState.handleToggle}>
      {children}

      <PopoverTrigger className="cursor-default" asChild>
        <span className="absolute opacity-0 h-full right-0" />
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="pl-4 pr-3 py-3 !bg-dark-base shadow-modal w-[520px] max-w-[90vw] outline-none"
        onInteractOutside={(e) => {
          const target = e.target
          if (!(target instanceof HTMLElement)) return
          if (target.role === `filter-trigger-${block.id}`) {
            e.preventDefault()
          }
        }}
      >
        <p className="font-medium text-dark-muted mb-1 text-sm">Filter</p>
        <div className="space-y-3">
          {!selectedView?.filter.length ? (
            <p className="text-dark-dim text-sm h-7 leading-7">
              No filters applied to this view yet
            </p>
          ) : (
            selectedView.filter.map((filter, index) => {
              if (filter.type === 'and' || filter.type === 'or') {
                throw new Error('and/or not supported yet')
              }

              return (
                <Filter
                  key={filter.by + filter.type + index}
                  filter={filter}
                  possibleBlocksToFilter={possibleBlocksToFilter}
                  onDeleteFilter={async () => {
                    if (!replicache || editorMode !== 'write:all') return

                    const params: UpdateListViewParams = {
                      id: selectedView?.id,
                      blockId: block.id,
                      targetBlockId_withinTree,
                      idSeed: getId(),
                      data: {
                        filter: selectedView.filter.filter(
                          (_, i) => i !== index
                        ),
                      },
                      isoNow: new Date().toISOString(),
                      path,
                    }
                    const { undo } =
                      await replicache.mutate.updateListView(params)
                    undoManager.current?.push({
                      redo: [
                        focusOpTable,
                        { op: 'updateListView', args: params },
                      ],
                      undo: [
                        focusOpTable,
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: undo },
                        },
                      ],
                    })
                  }}
                  onUpdateFilterBy={async (by) => {
                    if (
                      !replicache ||
                      !blockTreeManager ||
                      editorMode !== 'write:all'
                    ) {
                      return
                    }

                    let newFilterByType = 'text'
                    if (by === '__hidden') newFilterByType = 'boolean'
                    else if (by === '__title') newFilterByType = 'text'
                    else {
                      newFilterByType = blockTreeManager.getNode(by, {
                        required: true,
                      }).value.type
                    }

                    const params: UpdateListViewParams = {
                      id: selectedView?.id,
                      blockId: block.id,
                      targetBlockId_withinTree,
                      idSeed: getId(),
                      data: {
                        filter: selectedView.filter.map((f, i) => {
                          if (f.type === 'and' || f.type === 'or') {
                            throw new Error('and/or not supported yet')
                          }

                          // this filter was not modified, do nothing with it
                          if (index !== i) return f

                          // check if the selected filter type is allowed in the new filter by
                          const selectedFilterType =
                            LIST_VIEW_FILTER_TYPES.find(
                              (ft) => ft.value === filter.type
                            )
                          const filterTypeShouldBeUpdated =
                            !selectedFilterType?.blocks.includes(
                              newFilterByType
                            )

                          const newValue =
                            newFilterByType === 'boolean' &&
                            !['true', 'false'].includes(f.value.toLowerCase())
                              ? 'true'
                              : f.value

                          // happy path: matches, do nothing but update the target of the filter
                          if (!filterTypeShouldBeUpdated) {
                            return { ...f, value: newValue, by }
                          }

                          const firstFilterForThisBlockType =
                            LIST_VIEW_FILTER_TYPES.find((filter) =>
                              filter.blocks.includes(newFilterByType)
                            )

                          if (!firstFilterForThisBlockType) {
                            return { ...f, by, value: newValue }
                          }

                          // needs an update, perform it
                          return {
                            by,
                            type: firstFilterForThisBlockType.value,
                            value: newValue,
                          }
                        }),
                      },
                      isoNow: new Date().toISOString(),
                      path,
                    }
                    const { undo } =
                      await replicache.mutate.updateListView(params)
                    undoManager.current?.push({
                      redo: [
                        focusOpTable,
                        { op: 'updateListView', args: params },
                      ],
                      undo: [
                        focusOpTable,
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: undo },
                        },
                      ],
                    })
                  }}
                  onUpdateFilterType={async (type) => {
                    if (!replicache || editorMode !== 'write:all') return

                    const params: UpdateListViewParams = {
                      id: selectedView?.id,
                      blockId: block.id,
                      targetBlockId_withinTree,
                      idSeed: getId(),
                      data: {
                        filter: selectedView.filter.map((f, i) => {
                          if (f.type === 'and' || f.type === 'or') {
                            throw new Error('and/or not supported yet')
                          }

                          if (index !== i) return f
                          return {
                            ...f,
                            type,
                          } as ListView['filter'][number]
                        }),
                      },
                      isoNow: new Date().toISOString(),
                      path,
                    }
                    const { undo } =
                      await replicache.mutate.updateListView(params)
                    undoManager.current?.push({
                      redo: [
                        focusOpTable,
                        { op: 'updateListView', args: params },
                      ],
                      undo: [
                        focusOpTable,
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: undo },
                        },
                      ],
                    })
                  }}
                  onUpdateFilterValue={async (value) => {
                    // remove this sorting method
                    if (!replicache || editorMode !== 'write:all') return

                    const params: UpdateListViewParams = {
                      id: selectedView?.id,
                      blockId: block.id,
                      targetBlockId_withinTree,
                      idSeed: getId(),
                      data: {
                        filter: selectedView.filter.map((f, i) => {
                          if (index !== i) return f
                          return { ...f, value }
                        }),
                      },
                      isoNow: new Date().toISOString(),
                      path,
                    }
                    const { undo } =
                      await replicache.mutate.updateListView(params)
                    undoManager.current?.push({
                      redo: [
                        focusOpTable,
                        { op: 'updateListView', args: params },
                      ],
                      undo: [
                        focusOpTable,
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: undo },
                        },
                      ],
                    })
                  }}
                />
              )
            })
          )}
          {editorMode === 'write:all' && (
            <Button
              unstyled
              focusStyled
              className="text-dark-muted mt-4 text-xs font-medium hover:text-dark-dim flex items-center gap-x-1 leading-none cursor-default"
              icon={<PlusIcon width={16} height={16} />}
              iconSide="left"
              onClick={async () => {
                if (!replicache || editorMode !== 'write:all') return

                const params: UpdateListViewParams = {
                  id: selectedView?.id,
                  blockId: block.id,
                  targetBlockId_withinTree,
                  idSeed: getId(),
                  data: {
                    filter: [
                      ...(selectedView?.filter ?? []),
                      { type: 'contains', by: '__title', value: '' },
                    ],
                  },
                  isoNow: new Date().toISOString(),
                  path,
                }
                const { view, undo } =
                  await replicache.mutate.updateListView(params)

                if (view && selectedView?.id !== view.id) {
                  await updateSelectedView(view.id)
                }

                undoManager.current?.push({
                  redo: [focusOpTable, { op: 'updateListView', args: params }],
                  undo: [
                    focusOpTable,
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: undo },
                    },
                  ],
                })
              }}
            >
              Add {hasFilters ? 'another' : ''} filter
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

const Filter = React.forwardRef<
  HTMLDivElement,
  {
    filter: ListView['filter'][number]
    possibleBlocksToFilter: AtLeast<Block, 'id' | 'title' | 'type'>[]
    onDeleteFilter: () => Promise<void>
    onUpdateFilterValue: (value: string) => Promise<void>
    onUpdateFilterBy: (value: string) => Promise<void>
    onUpdateFilterType: (
      value: ListView['filter'][number]['type']
    ) => Promise<void>
  }
>(
  (
    {
      filter,
      possibleBlocksToFilter,
      onDeleteFilter,
      onUpdateFilterValue,
      onUpdateFilterBy,
      onUpdateFilterType,
    },
    ref
  ) => {
    if (filter.type === 'and' || filter.type === 'or') {
      throw new Error('and/or not supported yet')
    }

    const { value, onChange } = useSyncedValue({
      value: filter.value,
      setValue: onUpdateFilterValue,
      shouldSync: () => false, // never sync.
      rateLimitMs: 1000,
      rateLimitType: 'debounce',
    })
    const editorMode = useEditorMode()

    const blockTreeManager = useWorkingTreeStore((state) => state.tree)
    const block = blockTreeManager?.getNode(filter.by)?.value
    let filterType: BlockType = 'text'
    if (filter.by === '__hidden') filterType = 'boolean'
    else if (filter.by === '__title') filterType = 'text'
    else if (block?.type) filterType = block.type

    return (
      <div className="flex items-center gap-x-2" ref={ref}>
        <Select defaultValue={filter.by} onValueChange={onUpdateFilterBy}>
          <SelectTrigger
            className="w-1/4 shrink-0"
            disabled={editorMode !== 'write:all'}
          >
            <SelectValue />
            <BlockIcon
              block={{ type: filterType }}
              iconProps={{ width: 12, height: 12, className: 'shrink-0' }}
            />
          </SelectTrigger>
          <SelectContent>
            {possibleBlocksToFilter.map((block) => (
              <SelectItem value={idToSourceBlockId(block)} key={block.id}>
                {block.title}
                {Boolean(block.id.startsWith('__')) && (
                  <span className="text-[11px] text-dark-muted">
                    &nbsp;(sys)
                  </span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          defaultValue={filter.type}
          onValueChange={onUpdateFilterType}
          disabled={editorMode !== 'write:all'}
        >
          <SelectTrigger className="w-1/3 shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LIST_VIEW_FILTER_TYPES.filter((option) =>
              option.blocks.includes(filterType)
            ).map((option, index) => (
              <SelectItem value={option.value} key={index}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <FilterInput
          readonly={editorMode !== 'write:all'}
          block={block ?? ({ type: filterType } as AtLeast<Block, 'type'>)}
          filter={{ ...filter, value }}
          onChange={onChange}
        />

        {editorMode === 'write:all' && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              onDeleteFilter()
            }}
          >
            <Cross1Icon className="text-dark-faint" width={16} height={16} />
          </IconButton>
        )}
      </div>
    )
  }
)

const FilterInput = (
  props: ({ block: AtLeast<Block, 'type'> } | { block: DateBlock }) & {
    filter: ListView['filter'][number]
    onChange: (value: string) => void
    readonly?: boolean
  }
) => {
  if (props.block.type === 'boolean') {
    if (props.filter.type === 'and' || props.filter.type === 'or') {
      throw new Error('and/or not supported yet')
    }

    return (
      <Select onValueChange={props.onChange} value={props.filter.value}>
        <SelectTrigger
          disabled={props.readonly}
          className="w-1/3 shrink-0 flex-1"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">True</SelectItem>
          <SelectItem value="false">False</SelectItem>
        </SelectContent>
      </Select>
    )
  }

  const inputProps = getFilterInputProps({
    block: props.block,
    filter: props.filter,
  })

  return (
    <Input
      {...inputProps}
      className="!rounded-lg"
      onChange={(e) => props.onChange(e.target.value)}
      readOnly={inputProps.readOnly || props.readonly}
    />
  )
}
